import { GenericTableTranslations } from './generic-table-translations.interface';

/**
 * Default English translations for the Generic Table component
 * This serves as the fallback translation set and defines all available translation keys
 */
export const DEFAULT_GENERIC_TABLE_TRANSLATIONS: GenericTableTranslations = {
  // Loading and state messages
  loading: 'Loading...',
  noDataAvailable: 'No data available',

  // Error state
  error: {
    loadFailed: 'The data could not be loaded.',
    retry: 'Retry'
  },

  // Pagination
  pagination: {
    entries: 'entries',
    show: 'Show',
    firstPage: 'First page',
    lastPage: 'Last page',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    summaryTemplate: '{from} - {to} of {total} entries',
    noEntriesTemplate: 'No entries found',
    all: 'All',
  },

  // Column settings
  columnSettings: {
    title: 'Column Settings',
    close: 'Close menu',
    hintBoth: 'Use the checkboxes to show or hide columns. Use the arrow buttons to change the order of columns in the table.',
    hintVisibility: 'Use the checkboxes to show or hide columns in the table.',
    hintReorder: 'Use the arrow buttons to change the order of columns in the table.',
  },

  // Sorting
  sorting: {
    sortAscending: 'Sort ascending',
    sortDescending: 'Sort descending',
    clearSort: 'Clear sort',
    clearAllSorts: 'Clear all sorts',
    sort: 'Sort',
    ascending: 'ascending',
    descending: 'descending',
    sortedBy: 'Sorted {direction}',
    sortedByWithPrecedence: 'Sorted {direction} (precedence: {precedence})'
  },

  // Filtering
  filtering: {
    filter: 'Filter',
    clearFilter: 'Clear filter',
    searchPlaceholder: "Filter '{column}'...",
    filteredPrefix: 'Filtered: ',
    selectedItems: '{count} selected'
  },

  // Quick filters
  quickfilters: {
    label: 'Quick Filters:',
    applyFilter: 'Apply \'{label}\' filter',
    addCustom: 'Add custom quick filter',
    removeCustom: 'Remove custom quick filter',
    createTitle: 'Create Custom Quick Filter',
    deleteTitle: 'Delete Quick Filter',
    deleteMessage: 'Are you sure you want to delete the quick filter \'{label}\'?',
    nameLabel: 'Filter Name',
    namePlaceholder: 'Enter a name for this filter...',
    colorLabel: 'Color',
    cancel: 'Cancel',
    create: 'Create',
    delete: 'Delete',
    nameRequired: 'Please enter a name for the filter',
    colors: {
      '': 'Default',
      red: 'Red',
      green: 'Green',
      blue: 'Blue',
      yellow: 'Yellow',
      orange: 'Orange',
      purple: 'Purple',
      brown: 'Brown',
    },
  },

  // Filter operators - Text
  textOperators: {
    equals: 'Equals',
    notEquals: 'Not equals',
    contains: 'Contains',
    startsWith: 'Starts with',
    endsWith: 'Ends with',
    greaterThan: 'Greater than',
    lessThan: 'Less than',
    greaterThanOrEqual: 'Greater than or equal',
    lessThanOrEqual: 'Less than or equal',
  },

  // Filter operators - Date
  dateOperators: {
    isOn: 'Is on',
    isNotOn: 'Is not on',
    isAfter: 'Is after',
    isBefore: 'Is before',
    isOnOrAfter: 'Is on or after',
    isOnOrBefore: 'Is on or before',
  },

  // Column management
  columns: {
    autoSizeColumn: 'Auto-size Column',
    hideColumn: 'Hide Column',
    moveUp: 'Move up',
    moveDown: 'Move down',
    moveUpDisabled: 'Cannot move \'{column}\' up',
    moveDownDisabled: 'Cannot move \'{column}\' down',
    atLeastOneRequired: '(At least one column is required)',
    fixedPosition: '(Fixed position)',
    column: 'Column',
    hideColumnTitle: 'Hide \'{column}\' column',
    showColumnTitle: 'Show \'{column}\' column',
    visibilityRequiredTitle: '\'{column}\' must remain visible',
  },

  // Accessibility and ARIA labels
  aria: {
    clickToSortBy: 'Click to sort by \'{column}\'',
    optionsFor: 'Options for \'{column}\'',
    filterOperatorFor: 'Filter operator for \'{column}\'',
    filter: 'Filter \'{column}\'',
    clearFilterFor: 'Clear filter for \'{column}\'',
    filterByDate: 'Filter by \'{column}\' date',
    clearDateFilterFor: 'Clear date filter for \'{column}\'',
    filterOptionsFor: 'Filter options for \'{column}\'',
    toggleFilterFor: 'Toggle filter for {option}',
    clearAllFiltersFor: 'Clear all filters for \'{column}\'',
    additionalFilterOptions: 'Additional filter options',
    autoSizeColumn: 'Auto-size \'{column}\' column',
    hideColumn: 'Hide \'{column}\' column',
    columnVisibilityOptions: 'Column visibility options',
    moveColumnUp: 'Move \'{column}\' up',
    moveColumnDown: 'Move \'{column}\' down',
    moveColumnUpDisabled: '\'{column}\' is already at the top',
    moveColumnDownDisabled: '\'{column}\' is already at the bottom',
    hideColumnCheckbox: 'Hide \'{column}\' column',
    showColumnCheckbox: 'Show \'{column}\' column',
    columnVisibleRequired: '\'{column}\' column must remain visible',
    resizeColumn: 'Resize \'{column}\'',
    pagination: 'Pagination',
    goToPage: 'Go to page {page}',
    currentPage: 'Current page, page {page}',
  },

  // Tooltips and help text
  tooltips: {
    reloadData: 'Reload table data',
    openColumnSettings: 'Open column settings',
    dragToResize: 'Drag to resize column, double-click to auto-size',
    autoFitColumns: 'Auto-fit columns to available space',
  },
};
